import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { FileDown, Menu, X } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { toast } from 'sonner';

const Header = ({ contentRef }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const exportToPDF = async () => {
    if (!contentRef.current) return;
    
    setIsGenerating(true);
    toast.info('Generating PDF... This may take a moment');
    
    try {
      const element = contentRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#FDFBF7'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      pdf.save('LIVENEAT-Investment-Proposal.pdf');
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 crystal-glass">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_vending-returns/artifacts/6zfihjrd_1000050582.png" 
              alt="LIVENEAT Logo" 
              className="h-12 w-auto"
            />
          </div>
          
          <nav className="hidden md:flex items-center space-x-8">
            <button onClick={() => scrollToSection('overview')} className="text-sm font-medium text-[#064E3B] hover:text-[#10B981] transition-colors">
              Overview
            </button>
            <button onClick={() => scrollToSection('calculator')} className="text-sm font-medium text-[#064E3B] hover:text-[#10B981] transition-colors">
              Calculator
            </button>
            <button onClick={() => scrollToSection('returns')} className="text-sm font-medium text-[#064E3B] hover:text-[#10B981] transition-colors">
              Returns
            </button>
            <button onClick={() => scrollToSection('contact')} className="text-sm font-medium text-[#064E3B] hover:text-[#10B981] transition-colors">
              Contact
            </button>
          </nav>

          <div className="hidden md:flex items-center space-x-4">
            <Button 
              variant="outline" 
              onClick={exportToPDF}
              disabled={isGenerating}
              data-testid="export-pdf-button"
              className="border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white"
            >
              <FileDown className="mr-2 h-4 w-4" />
              {isGenerating ? 'Generating...' : 'Export PDF'}
            </Button>
            <Button 
              onClick={() => scrollToSection('contact')}
              data-testid="invest-now-button"
              className="bg-[#10B981] hover:bg-[#059669] text-white font-semibold px-6"
            >
              Invest Now
            </Button>
          </div>

          <button 
            className="md:hidden text-[#064E3B]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-[#E5E7EB]">
            <div className="flex flex-col space-y-4">
              <button onClick={() => scrollToSection('overview')} className="text-sm font-medium text-[#064E3B] text-left">
                Overview
              </button>
              <button onClick={() => scrollToSection('calculator')} className="text-sm font-medium text-[#064E3B] text-left">
                Calculator
              </button>
              <button onClick={() => scrollToSection('returns')} className="text-sm font-medium text-[#064E3B] text-left">
                Returns
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-sm font-medium text-[#064E3B] text-left">
                Contact
              </button>
              <Button onClick={exportToPDF} disabled={isGenerating} variant="outline" className="w-full border-[#10B981] text-[#10B981]">
                <FileDown className="mr-2 h-4 w-4" />
                {isGenerating ? 'Generating...' : 'Export PDF'}
              </Button>
              <Button onClick={() => scrollToSection('contact')} className="w-full bg-[#10B981] hover:bg-[#059669] text-white">
                Invest Now
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
